import { useState } from 'react';

import { AxiosError, isAxiosError } from 'core';
import { CsvImportForm } from 'core/Components';
import { columnsCreator } from 'core/Utils';
import { Modal, notifications } from 'ui';

import { useCrowdfundingProvider, useManagementTranslations } from '../../../../../../Context';
import { PeerParticipant } from '../../../../../../api';
import { useListCampaignPeers } from '../../../../../../api/new/campaigns/peers/get';
import {
  ImportParticipantResponse,
  useImportBulkParticipantActivity,
} from '../../../../../../api/new/campaigns/peers/update';
import { CSVErrorModal } from '../../Settings/Peers/CSVErrorModal';

type ActivityCSV = {
  units_completed: number;
  first_name: string;
  last_name: string;
  email: string;
  uuid: string;
};

const activityTemplateColumnCreator = columnsCreator<ActivityCSV>();
const activityTemplateColumns = [
  activityTemplateColumnCreator.create({
    header: 'First Name',
    cell: (row) => row.first_name,
  }),
  activityTemplateColumnCreator.create({
    header: 'Last Name',
    cell: (row) => row.last_name,
  }),
  activityTemplateColumnCreator.create({
    header: 'Email',
    cell: (row) => row.email,
  }),
  activityTemplateColumnCreator.create({
    header: 'Units Completed',
    cell: (row) => row.units_completed,
  }),
  activityTemplateColumnCreator.create({
    header: 'UUID',
    cell: (row) => row.uuid,
  }),
];

type ParticipantActivityBulkImportModalContentProps = {
  onClose: () => void;
};

const ParticipantActivityBulkImportModalContent = ({
  onClose,
}: ParticipantActivityBulkImportModalContentProps) => {
  const { t } = useManagementTranslations();
  const { campaignId, causeId } = useCrowdfundingProvider();
  const { data } = useListCampaignPeers({
    campaignId,
    causeId,
    peer_type: 'participants',
    pagination: {
      limit: 1000,
    },
  });
  const { mutate: bulkImportActivity, isPending: isLoading } = useImportBulkParticipantActivity();
  const [erroredParticipantsData, setErroredParticipantsData] = useState<
    ImportParticipantResponse['participants'] | undefined
  >();

  if (!campaignId || !causeId) {
    return null;
  }

  return (
    <>
      <CsvImportForm
        id="import-activity-csv"
        title={`${t('participant')} Activity`}
        templateColumns={activityTemplateColumns}
        data={((data?.participants ?? []) as PeerParticipant[]).map((participant) => ({
          first_name: participant.first_name,
          last_name: participant.last_name,
          email: participant.email,
          uuid: participant.uuid,
        }))}
        onImport={(data) => {
          bulkImportActivity(
            {
              campaignId,
              causeId,
              activityLog: data
                .map((row) => ({
                  value: Number(row?.['Units Completed']),
                  uuid: row?.['UUID'] ?? '',
                }))
                // Filter out rows with no value - also with the added benefit of filtering out
                // any activity values that are letters
                .filter((row) => row.value && row.value > 0),
            },
            {
              onSuccess: (data) => {
                const { participants } = data;
                const erroredParticipants = participants.filter(
                  (participant) => 'errors' in participant
                );

                if (erroredParticipants.length === 0) {
                  onClose();
                } else {
                  setErroredParticipantsData(erroredParticipants);
                }

                notifications.show({
                  variant: 'success',
                  message: 'Successfully imported participant activity',
                });
              },
              onError: (e) => {
                const error = e as Error | AxiosError;
                if (isAxiosError(error)) {
                  notifications.show({
                    variant: 'error',
                    message: error.response?.data?.error ?? 'Failed to import participant activity',
                  });
                }

                onClose();
              },
            }
          );
        }}
        loading={isLoading}
        onClose={onClose}
      />
      <CSVErrorModal
        erroredCSVData={
          erroredParticipantsData?.map((participant) => ({
            first_name: participant.first_name,
            last_name: participant.last_name,
            email: participant.email,
            uuid: participant.uuid,
            errors: participant.errors,
          })) ?? []
        }
        opened={erroredParticipantsData !== undefined}
        onClose={() => {
          setErroredParticipantsData(undefined);
          onClose();
        }}
        PeerType="participants"
      />
    </>
  );
};

type ParticipantActivityBulkImportModalProps = {
  opened: boolean;
  onClose: () => void;
};

export const ParticipantActivityBulkImportModal = ({
  opened,
  onClose,
}: ParticipantActivityBulkImportModalProps) => {
  const { t } = useManagementTranslations();

  return (
    <Modal title={`Import ${t('participant')} Activity from CSV`} opened={opened} onClose={onClose}>
      <ParticipantActivityBulkImportModalContent onClose={onClose} />
    </Modal>
  );
};
